import { defineStore, acceptHMRUpdate } from "pinia";
import { useToast } from "vue-toastification";
import dayjs from "dayjs";
import { useAppStore as appStore } from "@/store";
import APIRequest from "@/libs/cloud_auth";

export const useSchedulerStore = defineStore("scheduler", {
  state: () => ({
    isLoading: false,
    calendar: null,
    campaigns: [],
    rawCampaigns: null,
    currentViewType: 'dayGridMonth',
  }),
  getters: {},
  actions: {
    async getCampaigns() {
      try {
        this.isLoading = true;
        const { data } = await APIRequest(`gadgets/campaign/campaign_list/?ncm=${appStore().getCurrentTenant}`);
        this.rawCampaigns = data.campaigns;
        this.generateEvents();
        this.isLoading = false;
      } catch(error) {
        console.error(error);
      }
    },
    generateEvents() {
      if (!this.rawCampaigns) return [];
      this.campaigns = this.rawCampaigns.flatMap(campaign => {
        const color = this.hexToRgb(campaign.color);
        const colorWithOpacity = `rgba(${color.r}, ${color.g}, ${color.b}, 0.25)`;

        // Support new and old structure fallback
        const schedules = campaign.schedules && campaign.schedules.length > 0
          ? campaign.schedules
          : campaign.schedules_data && campaign.schedules_data.length > 0
            ? campaign.schedules_data
            : [{ start_date: campaign.start_date, end_date: campaign.end_date, recurrency_pattern: campaign.recurrency_pattern, all_day: campaign.all_day }];

        // Filter out empty placeholder schedules first
        const validSchedules = schedules.filter(s => s.start_date && s.end_date);
        if (validSchedules.length === 0) return [];

        const isMonthView = this.currentViewType === 'dayGridMonth';
        const schedulesToRender = isMonthView ? [validSchedules[0]] : validSchedules;

        return schedulesToRender.map(schedule => {
          let duration = '';
          const pattern = schedule.recurrency_pattern || campaign.recurrency_pattern; // Prefer schedule pattern
          if(pattern) {
            const startDateTime = dayjs(schedule.start_date);
            const endDateTime = startDateTime.startOf('day').add( dayjs(schedule.end_date).hour(), 'hour').add( dayjs(schedule.end_date).minute(), 'minute');

            duration = endDateTime.diff(startDateTime);
          }

          return {
            id: campaign.id,
            schedule_id: schedule.id,
            presentation: campaign.presentation,
            asset: campaign.asset,
            pattern: pattern ? pattern.replace(/Z/g, '') : undefined,
            title: campaign.name,
            contentPlaceholder: 'override presentation',
            color: colorWithOpacity,
            room: campaign.locations,
            mac_addresses: campaign.mac_addresses,
            start: schedule.start_date,
            start_date: schedule.start_date,
            end: schedule.end_date,
            end_date: schedule.end_date,
            schedules: validSchedules,
            schedules_data: validSchedules, // Legacy mapping
            all_day: schedule.all_day !== undefined ? schedule.all_day : campaign.all_day,
            ...(pattern ? {
              duration,
              rrule: pattern.replace(/Z/g, ''),
            } : {})
          };
        });
      });

      if (this.calendar) {
        let events = this.calendar.getEventSources();
        for (let i = 0; i < events.length; i++) { 
          events[i].remove(); 
        } 
        this.calendar.addEventSource(this.campaigns);
      }
    },
    async addCampaign(form) {
      let config = {
        presentation: form.presentation,
        asset: form.media,
        name: form.name,
        content_placeholder: 'override presentation',
        color: form.color,
        locations: form.room
      }

      try {
        const { data } = await APIRequest('gadgets/campaign/', 'post', config);
        
        if (form.schedules_data && form.schedules_data.length > 0) {
          const schedulePromises = form.schedules_data.map(schedule => {
            return APIRequest('gadgets/campaign_schedule/', 'post', {
              campaign: data.id,
              start_date: schedule.start_date,
              end_date: schedule.end_date,
              recurrency_pattern: schedule.recurrency_pattern,
              all_day: schedule.all_day
            });
          });
          await Promise.all(schedulePromises);
        }

        this.getCampaigns();
      } catch(error) {
        console.error(error);
      }

      return useToast().success("Campaign added successfully");
    },
    moveCampaign(id, start, end) {
      this.campaigns.forEach((p) => {
        if (p.id == id) {
          p.startDate = dayjs(start).format();
          p.endDate = dayjs(end).format();
        }
      });
    },
    deleteCampaign(id) {
      try {
        APIRequest(`gadgets/campaign/${id}/`,'delete').then(() => {
          this.getCampaigns()
        })
      } catch(error) {
        console.error(error);
      }

      return useToast().success("Campaign deleted successfully");
    },
    async duplicateCampaign(id) {
      try {
        const response = await APIRequest(`gadgets/campaign/duplicate_campaign/`, 'post', { id });

        if (!response || (response.status && response.status >= 400) || response.error) {
          throw response || new Error("Duplicate failed");
        }

        this.isLoading = true;
        await this.getCampaigns();
        this.isLoading = false;

        return response.data;
      } catch (error) {
        this.isLoading = false;
        throw error;
      }
    },
    async editCampaignSchedule(id, payload, fetch = true) {
      try {
        await APIRequest(`gadgets/campaign_schedule/${id}/`, 'patch', payload);
        if (fetch) {
          this.getCampaigns();
        }
      } catch(error) {
        console.error(error);
        throw error;
      }
    },
    async editCampaign(id, form) {
      let config = {
        presentation: form.presentation?.id || form.presentation,
        asset: form.media,
        name: form.name,
        content_placeholder: 'override presentation',
        recurrency_pattern: form.recurrency_pattern,
        color: form.color,
        locations: Array.isArray(form.room) ? form.room.map(r => r.id || r) : form.room,
        start_date: form.start_date !== undefined ? form.start_date : (form.schedules_data?.length ? form.schedules_data[0].start_date : null),
        end_date: form.end_date !== undefined ? form.end_date : (form.schedules_data?.length ? form.schedules_data[0].end_date : null),
        all_day: form.all_day !== undefined ? form.all_day : (form.schedules_data?.length ? form.schedules_data[0].all_day : false)
      }

      try {
        await APIRequest(`gadgets/campaign/${id}/`, 'put', config);

        const campaign = this.rawCampaigns?.find(c => c.id == id);
        const existingSchedules = new Set(
          (campaign?.schedules || campaign?.schedules_data || [])
            .map(s => s.id)
            .filter(Boolean)
        );
        
        if (existingSchedules.size > 0) {
          const deletePromises = Array.from(existingSchedules).map(scheduleId => 
            APIRequest(`gadgets/campaign_schedule/${scheduleId}/`, 'delete')
          );
          await Promise.all(deletePromises);
        }

        if (form.schedules_data && form.schedules_data.length > 0) {
          try {
            const schedulePromises = form.schedules_data.map(schedule => {
              return APIRequest('gadgets/campaign_schedule/', 'post', {
                campaign: id,
                start_date: schedule.start_date,
                end_date: schedule.end_date,
                recurrency_pattern: schedule.recurrency_pattern,
                all_day: schedule.all_day
              });
            });
            await Promise.all(schedulePromises);
          } catch (scheduleError) {
            console.error("Partial Failure: Old schedules deleted, but new ones failed to save", scheduleError);
            this.getCampaigns(); 
            throw scheduleError; 
          }
        }

        this.getCampaigns();
      } catch(error) {
        console.error(error);
        throw error;
      }
    },
    hexToRgb(hex) {
      // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
      let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      hex = hex.replace(shorthandRegex, (m, r, g, b) => {
        return r + r + g + g + b + b;
      });
    
      let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    }
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useSchedulerStore, import.meta.hot)
  );
}
